import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { dispatchLinkFor } from "@/lib/lrDirect/notifyLinks";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";

export type DispatchDocType = "bol" | "pod" | "rate_con" | "photo" | "other";

export interface DispatchDocument {
  id: string;
  dispatch_id: string;
  uploaded_by: string;
  doc_type: DispatchDocType;
  file_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
  uploader_name?: string;
}

export function useDispatchDocuments(dispatchId: string | undefined) {
  const [docs, setDocs] = useState<DispatchDocument[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!dispatchId) {
      setDocs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("direct_dispatch_documents" as any)
      .select("id, dispatch_id, uploaded_by, doc_type, file_path, file_name, file_size, mime_type, created_at")
      .eq("dispatch_id", dispatchId)
      .order("created_at", { ascending: false });
    const list = (data as any[] | null) ?? [];

    // hydrate uploader names
    const uploaderIds = Array.from(new Set(list.map((d) => d.uploaded_by).filter(Boolean)));
    let nameMap = new Map<string, string>();
    if (uploaderIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", uploaderIds);
      (profs ?? []).forEach((p: any) => {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "User";
        nameMap.set(p.id, name);
      });
    }
    setDocs(list.map((d) => ({ ...d, uploader_name: nameMap.get(d.uploaded_by) ?? "User" })));
    setLoading(false);
  }, [dispatchId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // realtime
  useEffect(() => {
    if (!dispatchId) return;
    const channel = supabase
      .channel(`dispatch-docs-${dispatchId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "direct_dispatch_documents", filter: `dispatch_id=eq.${dispatchId}` },
        () => refresh()
      )
      .subscribe((status) => { if (status === "SUBSCRIBED") refresh(); });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [dispatchId, refresh]);
  useRealtimeSelfHeal(refresh, !!dispatchId);


  return { docs, loading, refresh };
}

export async function getDispatchDocSignedUrl(filePath: string): Promise<string | null> {
  const { data } = await supabase.storage.from("dispatch-documents").createSignedUrl(filePath, 300);
  return data?.signedUrl ?? null;
}

/**
 * Delete a dispatch document: storage object FIRST, then the DB row.
 * Both steps are guarded — a failure surfaces an error instead of a silent
 * half-delete. Access is enforced by RLS (load's shipper owner + awarded
 * broker company members only), on both storage.objects and the table.
 */
export async function deleteDispatchDocument(args: {
  documentId: string;
  filePath: string;
  dispatchId: string;
  actorId: string;
  fileName: string;
  docType: DispatchDocType;
}): Promise<void> {
  const { documentId, filePath, dispatchId, actorId, fileName, docType } = args;

  const { data: removed, error: stErr } = await supabase.storage
    .from("dispatch-documents")
    .remove([filePath]);
  if (stErr) throw new Error(stErr.message || "Could not delete the file from storage.");
  // Supabase returns an empty array when RLS silently blocked the delete.
  if (!removed || removed.length === 0) {
    throw new Error("You don't have permission to delete this file.");
  }

  const { data: delRows, error: dbErr } = await supabase
    .from("direct_dispatch_documents" as any)
    .delete()
    .eq("id", documentId)
    .select("id");
  if (dbErr) throw new Error(dbErr.message || "Could not delete the document record.");
  if (!delRows || (delRows as any[]).length === 0) {
    throw new Error("You don't have permission to delete this document.");
  }

  // Timeline event (best-effort)
  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: actorId,
    event_type: "document_deleted",
    payload: { doc_type: docType, file_name: fileName, document_id: documentId },
  });
}


export interface UploadArgs {
  dispatchId: string;
  loadId: string;
  file: File;
  docType: DispatchDocType;
  uploaderId: string;
  counterpartyUserId: string | null;
  /** Shipper (load owner) user id — used to route the notify link. */
  shipperUserId?: string | null;
}

const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png", "image/heic", "image/heif", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export async function uploadDispatchDocument({
  dispatchId, loadId, file, docType, uploaderId, counterpartyUserId, shipperUserId,
}: UploadArgs): Promise<DispatchDocument> {
  if (file.size > MAX_BYTES) throw new Error("File exceeds 10MB limit.");
  const mime = file.type || "application/octet-stream";
  if (!ALLOWED_MIME.includes(mime.toLowerCase())) {
    throw new Error("Unsupported file type. Use PDF, JPG, PNG, or HEIC.");
  }
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${dispatchId}/${crypto.randomUUID()}-${safeName}`;

  const { error: upErr } = await supabase.storage
    .from("dispatch-documents")
    .upload(path, file, { contentType: mime, upsert: false });
  if (upErr) throw upErr;

  const { data: row, error: insErr } = await supabase
    .from("direct_dispatch_documents" as any)
    .insert({
      dispatch_id: dispatchId,
      uploaded_by: uploaderId,
      doc_type: docType,
      file_path: path,
      file_name: file.name,
      file_size: file.size,
      mime_type: mime,
    })
    .select("id, dispatch_id, uploaded_by, doc_type, file_path, file_name, file_size, mime_type, created_at")
    .single();
  if (insErr) throw insErr;

  // Timeline event (generic, every document)
  await supabase.from("direct_dispatch_events" as any).insert({
    dispatch_id: dispatchId,
    actor_id: uploaderId,
    event_type: "document_uploaded",
    payload: { doc_type: docType, file_name: file.name, file_size: file.size, document_id: (row as any)?.id ?? null },
  });

  // Phase 2: specific pod_uploaded event for the first POD only (idempotent).
  if (docType === "pod") {
    const { data: existingPod } = await supabase
      .from("direct_dispatch_events" as any)
      .select("id")
      .eq("dispatch_id", dispatchId)
      .eq("event_type", "pod_uploaded")
      .limit(1)
      .maybeSingle();
    if (!existingPod) {
      await supabase.from("direct_dispatch_events" as any).insert({
        dispatch_id: dispatchId,
        actor_id: uploaderId,
        event_type: "pod_uploaded",
        payload: { file_name: file.name, file_size: file.size, document_id: (row as any)?.id ?? null },
      });
    }
  }



  // Notify counterparty (best-effort, ignore errors)
  if (counterpartyUserId) {
    const docTypeLabel: Record<DispatchDocType, string> = {
      bol: "BOL", pod: "POD", rate_con: "Rate Confirmation", photo: "Photo", other: "Document",
    };
    const isPod = docType === "pod";
    notifyClient({
      recipientUserId: counterpartyUserId,
      eventType: isPod ? "pod_uploaded" : "dispatch_document_uploaded",
      inAppType: "dispatch_document_uploaded",
      title: `New ${docTypeLabel[docType]} uploaded`,
      body: file.name,
      link: dispatchLinkFor(counterpartyUserId, shipperUserId, loadId),
      loadId,
      metadata: { dispatch_id: dispatchId, doc_type: docType, file_name: file.name },
    });
  }


  return row as any;
}
